"use client";

import React, { useState } from "react";
import toast from "react-hot-toast";
import { alpha, Avatar, Box, Typography, styled } from "@mui/material";
import {
  RightColumn,
  SectionHeader,
  SectionContent,
  Label,
} from "./styles/SharedStyles";
import EditButton from "@components/shared/EditButton";
import MarkdownList from "@components/shared/MarkdownList";
import EditRoles from "../modals/EditRoles";
import {
  useRemoveCandidateRole,
  useUpdateCandidateRole,
  useAddCandidateRole,
  useReorderCandidateRoles,
} from "@lib/candidates/useRoleMutations";
import ReorderButtons from "@components/shared/ReorderButtons";
import { ProfileSkeleton } from "./skeletons";

const AddRoleWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%",
  height: "50px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: "4px",
  cursor: "pointer",

  "&:hover": {
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[100],
  },

  "& span": {
    color: theme.palette.secondary.main,
  },
}));

const Role = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  position: "relative",

  "&.no-border": {
    borderBottom: "none",
    marginBottom: 0,
    paddingBottom: 0,
  },
}));

const RoleDetails = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "top",
  gap: theme.spacing(2),
}));

const RoleProperties = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const RoleImage = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== "themeColor",
})(({ theme, themeColor }) => ({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  overflow: "hidden",
  alignSelf: "flex-start",
  backgroundColor: themeColor
    ? alpha(themeColor, 0.4)
    : theme.palette.primary.lighter,
  fontSize: "12px",
  fontWeight: "600",
  color: theme.palette.black?.main || theme.palette.text.primary,
}));

const RoleTitle = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  fontSize: 14,
  marginBottom: "8px",
  paddingTop: "7px",

  "& p": {
    margin: 0,
    lineHeight: 1,
  },
});

const RoleDescription = styled(Box)({
  lineHeight: 1.6,
});

const Roles = ({ candidate, isImporting }) => {
  const [editingRole, setEditingRole] = useState(null);
  const addRole = useAddCandidateRole();
  const updateRole = useUpdateCandidateRole();
  const removeRole = useRemoveCandidateRole();
  const reorderRoles = useReorderCandidateRoles();

  const handleEditRole = (role) => {
    setEditingRole(role);
  };

  const handleUpdateRole = (updatedRole) => {
    const toastId = toast.loading("Saving...");
    updateRole.mutate(
      { candidate, role: updatedRole },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to save");
        },
      }
    );
  };

  const handleAddRole = (event) => {
    const toastId = toast.loading("Adding...");
    event.preventDefault();
    addRole.mutate(
      {
        candidate,
        role: { name: "New role", role: "", time_period: "", description: "" },
      },
      {
        onSuccess: (data) => {
          toast.dismiss(toastId);
          setEditingRole(data.data.role);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to add role");
        },
      }
    );
  };

  const handleReorder = (index, direction) => {
    const roles = [...candidate.roles];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    [roles[index], roles[newIndex]] = [roles[newIndex], roles[index]];
    const toastId = toast.loading("Saving changes...");
    reorderRoles.mutate(
      {
        candidate,
        order: roles.map((r) => r.id),
      },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to reorder roles");
        },
      }
    );
  };

  const handleDeleteRole = (roleId) => {
    const toastId = toast.loading("Removing role...");
    removeRole.mutate(
      { candidate, roleId },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to remove role");
        },
      }
    );
  };

  return (
    <Box>
      <SectionContent>
        <Box>
          <RightColumn>
            <SectionHeader>
              <Typography variant="h2">Recent roles</Typography>
            </SectionHeader>
            {candidate.roles?.map((role, index) => (
              <Role
                key={role.id}
                className={
                  index === candidate.roles.length - 1 && !isImporting
                    ? "no-border"
                    : ""
                }
              >
                <ReorderButtons
                  onMoveUp={index > 0 ? () => handleReorder(index, "up") : null}
                  onMoveDown={
                    index < candidate.roles.length - 1
                      ? () => handleReorder(index, "down")
                      : null
                  }
                  sx={{ position: "absolute", top: "-8px", right: 96, height: 36 }}
                />
                <EditButton
                  onEdit={() => handleEditRole(role)}
                  sx={{ top: 0, right: 0 }}
                />
                <Label sx={{ lineHeight: 2.1 }}>Role details:</Label>
                <RoleDetails>
                  <RoleImage src={role.image} alt={role.name}>
                    {role.name?.substring(0, 2) ?? ""}
                  </RoleImage>
                  <RoleProperties>
                    <RoleTitle>
                      <p>{role.name}</p>
                      <p>{role.role}</p>
                      <p>{role.time_period}</p>
                    </RoleTitle>
                    <RoleDescription>
                      <MarkdownList content={role.description} />
                    </RoleDescription>
                  </RoleProperties>
                </RoleDetails>
              </Role>
            ))}
            {isImporting && (
              <Role className="no-border">
                <Label sx={{ lineHeight: 2.1 }}>Role details:</Label>
                <ProfileSkeleton />
              </Role>
            )}
            <AddRoleWrapper onClick={handleAddRole}>
              <span>Add new role +</span>
            </AddRoleWrapper>
          </RightColumn>
        </Box>
      </SectionContent>
      <EditRoles
        key={editingRole?.id}
        open={editingRole !== null}
        onClose={() => setEditingRole(null)}
        role={editingRole}
        onUpdate={handleUpdateRole}
        onDelete={handleDeleteRole}
        candidate={candidate}
      />
    </Box>
  );
};

export default Roles;
